import { z } from 'zod'

/**
 * Zod schema + validation rules for the door-selection step, extracted from
 * `SelectDoorsStep.tsx` so the gating logic is unit-testable in the node test
 * env (the component itself pulls in fb-ui, which can't load there). Re-exported
 * from `SelectDoorsStep` for back-compat, so existing imports are unchanged.
 */

export const selectDoorsStepFormSchema = z.object({
  apply_to: z
    .union([z.literal('entire_company'), z.literal('select_locations')])
    .default('entire_company'),
  door_ids: z.array(z.number()).optional(),
  is_all_doors_selected: z.boolean().optional(),
})

export interface SelectDoorsStepFormValues {
  apply_to: 'entire_company' | 'select_locations'
  door_ids?: number[]
  is_all_doors_selected?: boolean
}

/**
 * Door-selection gate (a Zod `superRefine`): when scoped to specific locations
 * the user must pick at least one door, and must NOT pick every door (that's
 * what "Entire company" is for). No-op when scoped to the entire company. Errors
 * attach to the `door_ids` path.
 */
export const checkDoorSelectionRules = (
  val: SelectDoorsStepFormValues,
  ctx: z.RefinementCtx
) => {
  if (val.apply_to === 'select_locations' && val.is_all_doors_selected) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'You cannot select all doors, use "Entire Company" instead',
      path: ['door_ids'],
    })
  } else if (
    val.apply_to === 'select_locations' &&
    (!val.door_ids || val.door_ids.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'You must select at least one door',
      path: ['door_ids'],
    })
  }
}
